/**
 * 分举报类型声量趋势 Hook
 */
import { useState, useCallback, useEffect } from 'react';
import {
  getCategoryMentionsTrend,
  ICategoryMentionsTrendResponse,
} from '@op/services/weeklyReport';
import { useCancelRequestHook } from '@op/hooks/useCancelRequest';
import { DBO_REQ } from '@op/utils/format';
import type { TRequestBackType } from '@op/utils/requestUtil';

interface IUseCategoryMentionsTrendOptions {
  autoFetch?: boolean;
}

type FetchDataFunc = (filter?: any) => Promise<void>;

export interface IUseCategoryMentionsTrendResult {
  data: ICategoryMentionsTrendResponse | null;
  loading: boolean;
  error: boolean;
  lineChartData: TRequestBackType | undefined;
  pieChartData: TRequestBackType | undefined;
  summary: ICategoryMentionsTrendResponse['summary'] | null;
  linkTime: { start_time: string; end_time: string } | null;
  list: ICategoryMentionsTrendResponse['list'] | null; // 原始 list 数据，用于获取中英文名
  fetchData: FetchDataFunc;
}

// ========== 数据处理函数 ==========

/**
 * 将 trend 预处理为长格式数据，供 DBO_REQ.listKeys 使用
 */
function preprocessTrendDataToLongFormat(data: ICategoryMentionsTrendResponse) {
  const { trend } = data;

  return trend.map(item => {
    const dataObj: any = { date: item.date };

    item.categories.forEach(it => {
      dataObj[it.category_name_en] = it.mentions;
    });

    return dataObj;
  });
}

/**
 * 使用 DBO_REQ.listKeys 将 trend 转换为折线图格式
 */
function transformTrendDataToLineChart(
  data: ICategoryMentionsTrendResponse,
  filter: any,
): TRequestBackType {
  const longFormatData = preprocessTrendDataToLongFormat(data);
  return DBO_REQ.moreKeys({
    timeline: longFormatData,
    timeKey: 'date',
    filter,
    timeline_of_start_time: data.trend_time.start_time,
    timeline_of_end_time: data.trend_time.end_time,
    needTable: true,
    moreLineTable: true,
  });
}

/**
 * 将 list 转换为饼图格式
 */
function transformListDataToPieChart(
  data: ICategoryMentionsTrendResponse,
): TRequestBackType {
  const { list } = data;

  return {
    series: list.map(item => ({
      name: item.category_name_en,
      data: [item.mentions],
    })),
    isEmpty: list.length === 0,
  };
}

// ========== Hook ==========

export const useCategoryMentionsTrend = (
  filter?: any,
  options?: IUseCategoryMentionsTrendOptions,
): IUseCategoryMentionsTrendResult => {
  const { autoFetch = true } = options || {};
  const [data, setData] = useState<ICategoryMentionsTrendResponse | null>(null);
  const [lineChartData, setLineChartData] = useState<TRequestBackType>();
  const [pieChartData, setPieChartData] = useState<TRequestBackType>();
  const [summary, setSummary] = useState<
    ICategoryMentionsTrendResponse['summary'] | null
  >(null);
  const [linkTime, setLinkTime] = useState<{
    start_time: string;
    end_time: string;
  } | null>(null);
  const [list, setList] = useState<
    ICategoryMentionsTrendResponse['list'] | null
  >(null);

  const { loading, error, wrapperCancelRequest } =
    useCancelRequestHook<ICategoryMentionsTrendResponse>({
      clear: () => {
        setData(null);
        setLineChartData(undefined);
        setPieChartData(undefined);
        setSummary(null);
        setLinkTime(null);
        setList(null);
      },
    });

  const fetchData = useCallback(
    async (filter?: any) => {
      const requestFilter = filter;

      if (!requestFilter?.start_time || !requestFilter?.end_time) {
        return;
      }

      try {
        const res = await wrapperCancelRequest(
          getCategoryMentionsTrend,
          requestFilter,
        );
        const resultData = res || null;
        setData(resultData);

        // 直接处理图表数据
        if (resultData) {
          setLineChartData(transformTrendDataToLineChart(resultData, filter));
          setPieChartData(transformListDataToPieChart(resultData));
          setSummary(resultData.summary || null);
          setLinkTime(resultData.link_time || null);
          setList(resultData.list || null);
        } else {
          setLineChartData(undefined);
          setPieChartData(undefined);
          setSummary(null);
          setLinkTime(null);
          setList(null);
        }
      } catch (err) {
        console.error('获取分举报类型声量趋势失败:', err);
      }
    },
    [filter],
  );

  useEffect(() => {
    if (!filter || !autoFetch) return;
    fetchData(filter);
  }, [filter, autoFetch, fetchData]);

  return {
    data,
    loading,
    error,
    lineChartData,
    pieChartData,
    summary,
    linkTime,
    list,
    fetchData,
  };
};

export default useCategoryMentionsTrend;
